import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, UserPlus, Users, X } from 'lucide-react';
import { toast } from '@/components/ui/toaster.jsx';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog.jsx';
import PossibleMatchButtons from '@/components/queue/PossibleMatchButtons.jsx';
import PersonSuggestionSheet, { sayAcceptError } from '@/components/queue/PersonSuggestionSheet.jsx';
import { listPeopleSuggestions, acceptPeopleSuggestion, dismissPeopleSuggestion } from '@/services/people-suggestions.service.js';
import { clearLabel, clearTitle } from '@/lib/queue-labels.js';
import { crmDeepLink } from '@/lib/links.js';

// PEOPLE TO CONFIRM (people-create-door R26, PLAN-ROUND3 section 2 'The block', Q14: its own
// separable change with R24 and R25). At the top of the queue page's Today section, one block
// for the rows that ask David about a person. An email is a delivery address, never identity
// (David 2026-10-20): nothing here links or merges on its own.
//   person_suggested  a person heard by a door: New person (the sheet), This is <candidate>,
//                     Someone else (a person id), Dismiss (a reason); an update: Review, Dismiss
//   possible_match    R6's Same person / Not the same (imported, never copied), the answer read
//                     back, and after Same person the one next step (the server derives it)
//   the legacy kinds  account_merge_suggested, typed_email_matches_client: today's clear
// Reverting R24 to R26 leaves the plain queue rows rendering every one of these kinds (QueueRow).

export const PEOPLE_TO_CONFIRM_KINDS = new Set(['person_suggested', 'possible_match', 'account_merge_suggested', 'typed_email_matches_client']);
const LEGACY_KINDS = new Set(['account_merge_suggested', 'typed_email_matches_client']);

export function confirmAnchor(key) {
  return `people-confirm-${String(key).replace(/[^A-Za-z0-9_-]/g, '-')}`;
}

// The call's queue item links to each of its open possible matches and its suggestion.
export function callConfirmLinks(call, items) {
  if (!call || call.kind !== 'phone_intake_call') return [];
  const refs = call.refs || {};
  const wanted = Array.isArray(refs.possible_match_refs) ? refs.possible_match_refs : [];
  const matches = items.filter((i) => i.kind === 'possible_match' && i.refs?.possible_match_ref && wanted.includes(i.refs.possible_match_ref));
  const out = matches.map((i, n) => ({ href: `#${confirmAnchor(i.key)}`, label: matches.length > 1 ? `Possible match ${n + 1}` : 'Its possible match' }));
  const sug = refs.suggestion_id ? items.find((i) => i.kind === 'person_suggested' && i.refs?.suggestion_id === refs.suggestion_id) : null;
  if (sug) out.push({ href: `#${confirmAnchor(sug.key)}`, label: 'Its suggestion' });
  return out;
}

const quiet = 'inline-flex min-h-[36px] shrink-0 items-center gap-1 rounded-[5px] border border-border bg-surface px-2.5 py-1.5 text-[12.5px] text-text-muted hover:border-border-strong hover:text-text disabled:opacity-50';
const primary = 'inline-flex min-h-[36px] shrink-0 items-center gap-1.5 rounded-[5px] border border-accent bg-accent px-3 py-1.5 text-[12.5px] text-white hover:brightness-110 disabled:opacity-50';
const field = 'w-full rounded border border-border bg-bg px-2 py-1.5 text-[13px] text-text';
const enc = encodeURIComponent;

function personLabel(c) {
  const name = [c.preferred_name || c.first_name, c.last_name].filter((v) => v && String(v).trim()).join(' ');
  return name || c.email || c.phone || `person ${c.id}`;
}

function RowText({ item, ageOf }) {
  return (
    <div className="min-w-0 flex-1">
      <p className="truncate text-sm font-medium text-text">{item.title}</p>
      <p className="text-[11px] text-text-muted">
        {item.deal_id ? (
          <Link to={`/account/${enc(item.deal_id)}/deal/${enc(item.deal_id)}`} className="hover:underline">{item.deal_name || `Deal ${item.deal_id}`}</Link>
        ) : (
          <span>No deal yet</span>
        )}
        {item.detail ? <span> {'·'} {item.detail}</span> : null}
        {item.age_at && ageOf ? <span> {'·'} {ageOf(item.age_at)}</span> : null}
      </p>
    </div>
  );
}

function DismissDialog({ open, onOpenChange, suggestionId, onDone }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  async function send() {
    if (!reason.trim()) return;
    setBusy(true);
    try {
      const r = await dismissPeopleSuggestion(suggestionId, reason.trim());
      if (r?.suggestion?.resolution === 'dismissed') toast.success('Dismissed.');
      else toast.info('The dismissal was sent; it could not be read back. Read the queue again.');
      onOpenChange(false);
    } catch (err) {
      toast.error(err.body?.message || err.body?.error || err.message || 'Could not dismiss');
    } finally {
      setBusy(false);
      onDone?.();
    }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Dismiss this suggestion</DialogTitle>
          <DialogDescription>Nothing is written about the person. Say why, for the record.</DialogDescription>
        </DialogHeader>
        <input autoFocus value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason" className={field} />
        <DialogFooter>
          <button type="button" onClick={() => onOpenChange(false)} className={quiet}>Cancel</button>
          <button type="button" disabled={busy || !reason.trim()} onClick={send} className={primary}>{busy ? 'Dismissing...' : 'Dismiss'}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// No people search exists on TRACON yet, so "Someone else" takes the person record's id.
function SomeoneElseDialog({ open, onOpenChange, suggestionId, onDone }) {
  const [personId, setPersonId] = useState('');
  const [busy, setBusy] = useState(false);
  async function send() {
    if (!personId.trim()) return;
    setBusy(true);
    try {
      const r = await acceptPeopleSuggestion(suggestionId, { existing_person_id: personId.trim() });
      if (r?.suggestion?.resolution) toast.success(`Recorded: this is person ${personId.trim()}.`);
      else toast.info('The answer was sent; it could not be read back. Read the queue again.');
      for (const w of r.warnings || []) toast.info(typeof w === 'string' ? w : (w.message || w.code));
      onOpenChange(false);
    } catch (err) {
      sayAcceptError(err);
    } finally {
      setBusy(false);
      onDone?.();
    }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Someone else on file</DialogTitle>
          <DialogDescription>The id of the person record this is. Nothing new is made.</DialogDescription>
        </DialogHeader>
        <input autoFocus value={personId} onChange={(e) => setPersonId(e.target.value)} placeholder="Person id" className={field} />
        <DialogFooter>
          <button type="button" onClick={() => onOpenChange(false)} className={quiet}>Cancel</button>
          <button type="button" disabled={busy || !personId.trim()} onClick={send} className={primary}>{busy ? 'Saving...' : 'This is the person'}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SuggestionRow({ item, s, loaded, failed, onDone, ageOf }) {
  const [sheet, setSheet] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const [other, setOther] = useState(false);
  const [busy, setBusy] = useState(false);
  const id = item.refs?.suggestion_id;
  const ready = !!s;
  const isUpdate = item.refs?.suggestion_kind === 'person_update';

  async function thisIs(c) {
    setBusy(true);
    try {
      const r = await acceptPeopleSuggestion(id, { existing_person_id: c.id });
      if (r?.suggestion?.resolution) toast.success(`Recorded: this is ${personLabel(c)}.`);
      else toast.info('The answer was sent; it could not be read back. Read the queue again.');
      for (const w of r.warnings || []) toast.info(typeof w === 'string' ? w : (w.message || w.code));
    } catch (err) {
      sayAcceptError(err);
    } finally {
      setBusy(false);
      onDone?.();
    }
  }

  return (
    <>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <UserPlus size={16} strokeWidth={1.5} className="shrink-0 text-text-dim" />
        <RowText item={item} ageOf={ageOf} />
      </div>
      {!loaded ? <p className="mt-1 text-[11px] text-text-dim">Reading the details...</p> : null}
      {loaded && !ready && !failed ? <p className="mt-1 text-[11px] text-warning">This suggestion was not in the details read. Read the queue again.</p> : null}
      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
        {isUpdate ? (
          <button type="button" disabled={!ready} onClick={() => setSheet(true)} className={primary}>Review</button>
        ) : (
          <>
            <button type="button" disabled={!ready || busy} onClick={() => setSheet(true)} className={primary}>New person</button>
            {(s?.candidate_people || []).map((c) => (c.missing
              ? <span key={c.id} className="text-[11px] text-warning">A person on file could not be read ({c.id})</span>
              : <button key={c.id} type="button" disabled={busy} title={[c.email, c.phone].filter(Boolean).join(', ')} onClick={() => thisIs(c)} className={quiet}><Check size={14} strokeWidth={1.5} /> {`This is ${personLabel(c)}`}</button>))}
            <button type="button" disabled={!ready || busy} onClick={() => setOther(true)} className={quiet}>Someone else</button>
          </>
        )}
        <button type="button" disabled={!id || busy} onClick={() => setDismissing(true)} className={quiet}><X size={14} strokeWidth={1.5} /> Dismiss</button>
      </div>
      {ready ? <PersonSuggestionSheet open={sheet} onOpenChange={setSheet} suggestion={s} onDone={() => { setSheet(false); onDone?.(); }} /> : null}
      {id ? <DismissDialog open={dismissing} onOpenChange={setDismissing} suggestionId={id} onDone={onDone} /> : null}
      {ready ? <SomeoneElseDialog open={other} onOpenChange={setOther} suggestionId={id} onDone={onDone} /> : null}
    </>
  );
}

// After Same person, the one next step: the server's answer when there is one (Q23: a moved
// pilot form says 'moved'), else the row's (utils/queue-derive possibleMatchNextStep).
function MergeLinks({ ids }) {
  return ids.map((cid, n) => (
    <span key={cid}>{n ? ' and ' : ''}<a href={crmDeepLink('Contacts', cid)} target="_blank" rel="noreferrer" className="text-accent hover:underline">Contact {cid}</a></span>
  ));
}
function NextStep({ item, next = null }) {
  const r = next && next.next_step ? next : (item.refs || {});
  if (r.next_step === 'moved') {
    return (
      <span className="text-[12px] text-text-muted">
        Moved: the pilot form is on the existing record now.
        {Array.isArray(r.contact_ids) ? <>{' '}Merge the two Contacts in the CRM when ready: <MergeLinks ids={r.contact_ids} /></> : null}
      </span>
    );
  }
  if (r.next_step === 'roster' && item.deal_id) {
    return <Link to={`/account/${enc(item.deal_id)}/deal/${enc(item.deal_id)}?door=roster`} className="text-[12px] text-accent hover:underline">Next: the deal&rsquo;s roster, where the pilot form is placed</Link>;
  }
  if (r.next_step === 'crm_merge' && Array.isArray(r.contact_ids)) {
    return (
      <span className="text-[12px] text-text-muted">
        Next: merge them in the CRM:{' '}
        <MergeLinks ids={r.contact_ids} />
      </span>
    );
  }
  return <span className="text-[12px] text-text-dim">Nothing was linked or merged; there is no next step here.</span>;
}

function MatchRow({ item, answer = null, next = null, onAnswered, ageOf }) {
  return (
    <>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <Users size={16} strokeWidth={1.5} className="shrink-0 text-text-dim" />
        <RowText item={item} ageOf={ageOf} />
        {answer ? null : <PossibleMatchButtons item={item} onDone={(stored, r) => onAnswered(item, stored, r)} />}
      </div>
      {answer ? (
        <div className="mt-1 flex flex-wrap items-center gap-x-3">
          <span className="text-[12px] font-medium text-text">{answer === 'same' ? 'You said: same person.' : 'You said: not the same.'}</span>
          {answer === 'same' ? <NextStep item={item} next={next} /> : null}
        </div>
      ) : null}
    </>
  );
}

function LegacyRow({ item, onClear, clearing, ageOf }) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
      <UserPlus size={16} strokeWidth={1.5} className="shrink-0 text-text-dim" />
      <RowText item={item} ageOf={ageOf} />
      {item.refs?.notification_id ? (
        <button type="button" title={clearTitle(item.kind)} disabled={clearing} onClick={() => onClear(item)} className={quiet}>
          <Check size={14} strokeWidth={1.5} /> {clearLabel(item.kind)}
        </button>
      ) : null}
    </div>
  );
}

export default function PeopleToConfirm({ items, onChanged, onClear, clearingId, ageOf }) {
  const [described, setDescribed] = useState(null);
  const [readError, setReadError] = useState('');
  const [answered, setAnswered] = useState([]);
  const suggestionIds = items.filter((i) => i.kind === 'person_suggested').map((i) => i.refs?.suggestion_id).join(',');

  // A failed read is said, never shown as an empty list; accept stays off until it reads.
  const read = useCallback(() => {
    listPeopleSuggestions()
      .then((r) => { setDescribed(new Map((r.suggestions || []).map((s) => [String(s.id), s]))); setReadError(''); })
      .catch((err) => { setDescribed(new Map()); setReadError(err.body?.error || err.message || 'read failed'); });
  }, []);
  useEffect(() => { if (suggestionIds) read(); }, [suggestionIds, read]);

  function changed() { if (suggestionIds) read(); onChanged?.(); }
  function matchAnswered(item, stored, r) {
    const next = r && r.next_step ? { next_step: r.next_step, contact_ids: r.contact_ids } : null;
    if (stored) setAnswered((a) => [...a.filter((x) => x.item.key !== item.key), { item, answer: stored, next }]);
    onChanged?.();
  }
  const done = new Set(answered.map((x) => x.item.key));
  const open = items.filter((i) => !done.has(i.key));

  return (
    <div className="rounded-card border border-border bg-surface px-3.5 py-2.5">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-text-dim">People to confirm {'·'} {open.length}</p>
      <p className="mt-0.5 text-[11px] text-text-dim">People heard by a door, and records that may be one person. Nothing is linked until you say so.</p>
      {readError ? <p className="mt-1 text-[12px] text-warning">The suggestions&rsquo; details could not be read ({readError}). Accept is off until they read; Dismiss still works.</p> : null}
      <div className="mt-2 space-y-2">
        {open.map((item) => (
          <div key={item.key} id={confirmAnchor(item.key)} className="rounded-card border border-border px-3 py-2">
            {item.kind === 'person_suggested' ? <SuggestionRow item={item} s={described ? described.get(String(item.refs?.suggestion_id)) : undefined} loaded={described !== null} failed={!!readError} onDone={changed} ageOf={ageOf} />
              : item.kind === 'possible_match' ? <MatchRow item={item} onAnswered={matchAnswered} ageOf={ageOf} />
                : LEGACY_KINDS.has(item.kind) ? <LegacyRow item={item} onClear={onClear} clearing={clearingId === item.refs?.notification_id} ageOf={ageOf} /> : null}
          </div>
        ))}
        {answered.map(({ item, answer, next }) => (
          <div key={`answered-${item.key}`} className="rounded-card border border-dashed border-border px-3 py-2">
            <MatchRow item={item} answer={answer} next={next} onAnswered={matchAnswered} ageOf={ageOf} />
          </div>
        ))}
      </div>
    </div>
  );
}
